import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosApi from "src/app/config";

const initialState = {
  ledgerData: null,
  events: [],
  summary: {},
  
  // Loading states
  isLoading: false,
  isError: false,
  isSuccess: false,
  error: null,
  
  // Legacy support
  ledgerList: [],
  totalBalance: 0,
  isGetLedgerLoading: false,
  isGetLedgerError: false,
  isGetLedgerSuccess: false,
  getLedgerError: null,
};

// New detailed ledger API
export const getDetailedLedger = createAsyncThunk(
  "ledger/getDetailedLedger",
  async (thunkAPI, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, clientOrganizationId, month, year } = thunkAPI ?? {};

      if (!clientOrganizationId || !month || !year) {
        return rejectWithValue("Required parameters missing: clientOrganizationId, month, year");
      }

      const response = await axiosApi({ token }).post(`/finance/ledger/detailed`, {
        clientOrganizationId,
        month: month.toString(),
        year: year.toString()
      });
      
      return fulfillWithValue(response.data);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Legacy support - keep existing getLedger
export const getLedger = createAsyncThunk(
  "company/getLedger",
  async (thunkAPI, { dispatch, getState, rejectWithValue, fulfillWithValue }) => {
    try {
      const { token, companyId, clientId } = thunkAPI ?? {};

      if (!companyId) {
        // log error
        return rejectWithValue("params missing");
      }
      const response = await axiosApi({ token }).post(`/accounts/getLedger`, {
        token,
        companyId,
        clientId,
        year: "2024"
      });
      return fulfillWithValue(JSON.parse(JSON.stringify(response.data)));
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const ledgerSlice = createSlice({
  name: "ledger",
  initialState,
  reducers: {
    clearSuccess: (state) => {
      state.isSuccess = false;
      state.isError = false;
      state.error = null;
    },
    clearError: (state) => {
      state.isError = false;
      state.error = null;
    },
    resetLoadingState: (state) => {
      state.isLoading = false;
      state.isError = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // New detailed ledger API handlers
    builder.addCase(getDetailedLedger.pending, (state) => {
      // Only set loading if we don't have existing data
      if (!state.events || state.events.length === 0) {
        state.isLoading = true;
      }
      state.isError = false;
      state.error = null;
      state.isSuccess = false;
    });
    builder.addCase(getDetailedLedger.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;
      state.isError = false;
      
      const data = action.payload?.data;
      state.ledgerData = data;
      state.events = data?.events || [];
      state.summary = data?.summary || {};
      
      // Legacy support
      state.totalBalance = data?.closing || 0;
      state.ledgerList = data?.events || [];
    });
    builder.addCase(getDetailedLedger.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.error = action.payload;
    });

    // Legacy ledger API handlers (keep existing)
    builder.addCase(getLedger.pending, (state) => {
      state.isGetLedgerLoading = true;
    });
    builder.addCase(getLedger.fulfilled, (state, action) => {
      state.isGetLedgerLoading = false;
      state.isGetLedgerSuccess = true;

      const { totalBalance, ledger } = action.payload?.data ?? {}

      state.totalBalance = totalBalance;
      state.ledgerList = ledger;
    });
    builder.addCase(getLedger.rejected, (state, action) => {
      state.isGetLedgerLoading = false;
      state.getLedgerError = action.payload.error;
      state.isGetLedgerError = true;
    });
  },
});

export const { clearSuccess, clearError, resetLoadingState } = ledgerSlice.actions;
export default ledgerSlice.reducer;
